<template>
  <div
    v-if="searchResults?.length"
    :class="$style.category"
    :data-type="type"
    data-results-category-v="3.5.0"
  >
    <header :class="$style.header">
      <h3 :class="$style.title">{{ title }}</h3>
      <template v-if="viewAllButton && total > searchResults?.length">
        <CallToAction :cta="viewAllButton" @click="onRequestViewAll" />
      </template>
    </header>

    <div :class="$style.container">
      <div ref="swiperRef" class="swiper">
        <div class="swiper-wrapper">
          <div
            v-for="result in computedResults"
            :key="result?.sys?.id"
            :class="['swiper-slide', $style.slide]"
          >
            <SearchResultsCard :type="type" :data="result" variant="carousel" />
          </div>
          <template v-if="computedResults && total > computedResults.length">
            <div :class="['swiper-slide', $style.slide]">
              <button :class="$style.viewAllButton" @click="onRequestViewAll">
                {{ viewAllButton?.fields?.label }}
                <i>
                  <IconArrowRight :class="$style.arrowIcon" />
                </i>
              </button>
            </div>
          </template>
        </div>
      </div>
      <button
        v-if="!isBeginning"
        :class="[$style.button, $style.prev]"
        aria-label="previous slide"
        @click="prevSlide"
      >
        <IconChevronLeft :class="$style.icon" />
      </button>
      <button
        v-if="!isEnd"
        :class="[$style.button, $style.next]"
        aria-label="next slide"
        @click="nextSlide"
      >
        <IconChevronRight :class="$style.icon" />
      </button>
    </div>

    <!-- <CtaWings v-if="viewAllButton && total > searchResults?.length">
      <CallToAction :cta="viewAllButton" @click="onRequestViewAll" />
    </CtaWings> -->
  </div>
</template>

<script setup lang="ts">
import type { Swiper as SwiperClass } from 'swiper'
import swiper from 'swiper'

import type {
  TypeDownloadableAssetWithoutUnresolvableLinksResponse,
  TypeCallToActionWithoutUnresolvableLinksResponse,
} from '~/types/contentful'

import type { TypeSearchResults } from './SearchResults.vue'
import type { TypeResultAssetData } from './SearchResultsAsset.vue'

import 'swiper/css'

const emit = defineEmits(['request-view-all'])

const props = withDefaults(
  defineProps<{
    viewAllButton: TypeCallToActionWithoutUnresolvableLinksResponse | undefined
    searchResults: TypeSearchResults[] | undefined
    title: string
    total: number
    type: 'article' | 'product' | 'event' | 'marketing-material' | 'document'
    slideWidth?: 'default' | 'wide'
  }>(),
  { slideWidth: 'default' },
)

const swiperRef = ref<(HTMLDivElement & { swiper: SwiperClass }) | null>(null)
const isBeginning = ref(false)
const isEnd = ref(false)

const computedResults = computed(
  (): TypeSearchResults[] | TypeResultAssetData[] | undefined => {
    if (props.type === 'product') {
      return props.searchResults as TypeResultAssetData[] | undefined
    }

    if (props.type === 'marketing-material') {
      if (props.searchResults?.length) {
        const collection = props.searchResults.flatMap((result) => {
          const r =
            result as TypeDownloadableAssetWithoutUnresolvableLinksResponse
          // const assets = r?.fields?.assets || []

          return {
            sys: {
              id: r?.sys?.id,
              contentType: {
                sys: {
                  id: r?.sys?.contentType?.sys?.id,
                },
              },
            },
            id: r?.sys?.id,
            image: r?.fields?.image,
            title: r?.fields?.title,
            cta: r?.fields?.cta,
          }
        })
        return collection
      }
    }
    return props.searchResults
  },
)

const onRequestViewAll = () => emit('request-view-all')

const prevSlide = () => {
  swiperRef.value?.swiper.slidePrev()
}

const nextSlide = () => {
  swiperRef.value?.swiper.slideNext()
}

onMounted(() => {
  if (!swiperRef.value) return

  const updateEdges = (sw: SwiperClass) => {
    // sw.isBeginning / sw.isEnd are usually correct, but we also guard with progress
    isBeginning.value = sw.isBeginning || sw.progress <= 0.001
    isEnd.value = sw.isEnd || sw.progress >= 0.999
  }

  const instance = new swiper(swiperRef.value, {
    slidesPerView: 'auto',
    spaceBetween: 16,
    watchOverflow: true,
    breakpoints: {
      1440: {
        slidesPerView: 5,
      },
    },
    on: {
      init: (sw) => updateEdges(sw),
      progress: (sw) => updateEdges(sw), // fires continuously while scrolling
      resize: (sw) => {
        sw.update()
        updateEdges(sw)
      },
      reachBeginning: () => {
        isBeginning.value = true
      },
      reachEnd: () => {
        isEnd.value = true
      },
      fromEdge: (sw) => updateEdges(sw),
    },
  })

  const ro = new ResizeObserver(() => {
    instance.update()
    updateEdges(instance)
  })
  ro.observe(swiperRef.value)
})
</script>

<style lang="scss" module>
.category {
  margin-bottom: 36px;

  @include media('md') {
    margin-bottom: 42px;
  }
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.title {
  font-family: var(--roboto-condensed);
  font-weight: bold;
  line-height: math.div(32, 26);
  @include fluid-type(26px, 32px);
}

.container {
  position: relative;
}

.slide {
  width: var(--search-result-card-width, 317px) !important;
  height: auto;

  @include media('xl') {
    width: var(--search-result-card-width, 267px) !important;
  }
}

.button {
  z-index: 1;
  background: rgba(black, 0.4);
  border: 2px solid white;
  border-radius: 50%;
  color: var(--white);

  display: flex;
  align-items: center;
  justify-content: center;
  width: 39px;
  height: 39px;

  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  padding: 1em;
  left: unset;
  right: unset;

  &:hover {
    background: rgba(black, 0.7);
  }

  &.prev {
    left: -19.5px;
  }

  &.next {
    right: -19.5px;
  }
}

.icon {
  height: 17px;
  width: 11px;
}

.arrowIcon {
  width: 28px;
  height: auto;
}

.viewAllButton {
  background: var(--purple);
  border: none;
  border-radius: 5px;

  color: var(--white);
  font-family: var(--roboto-condensed);
  font-weight: bold;
  text-transform: uppercase;

  padding: 0.5em 1.45em;
  width: 100%;
  height: 100%;
  cursor: pointer;

  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5em;

  @include fluid-type(24px, 24px);

  &:hover {
    filter: brightness(0.9);
  }
}
</style>
